import { reminders } from '../api';
import { createPageHeader } from '../components/form';
import { createDataTable, type Column } from '../components/table';
import { showToast } from '../components/toast';
import { confirmModal } from '../components/modal';
import type { Reminder } from '../types';

export async function remindersPage(): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('Reminders'));

  // Filters
  const filters = document.createElement('div');
  filters.className = 'flex gap-4 mb-6';
  filters.innerHTML = `
    <select id="rem-status-filter" class="input-field w-auto">
      <option value="" selected>All</option>
      <option value="pending">Pending</option>
      <option value="sent">Sent</option>
      <option value="acknowledged">Acknowledged</option>
    </select>
  `;
  page.appendChild(filters);

  const tableContainer = document.createElement('div');
  page.appendChild(tableContainer);

  const load = async () => {
    const status = (page.querySelector('#rem-status-filter') as HTMLSelectElement).value;
    const items = await reminders.list(status || undefined);
    renderTable(tableContainer, items, load);
  };

  filters.querySelector('select')!.addEventListener('change', load);
  await load();
  return page;
}

function renderTable(container: HTMLElement, items: Reminder[], reload: () => Promise<void>): void {
  container.innerHTML = '';
  const cols: Column<Reminder>[] = [
    { label: 'Type', key: 'type' },
    {
      label: 'Property', render: r => r.property_address
        ? `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property_address}</a>`
        : `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property_id.substring(0, 8)}...</a>`
    },
    { label: 'Message', render: r => `<span class="text-sm text-gray-600 truncate max-w-xs inline-block">${r.message || '—'}</span>` },
    { label: 'Due Date', key: 'due_date' },
    { label: 'Status', render: r => `<span class="badge badge-${r.status === 'acknowledged' ? 'green' : r.status === 'sent' ? 'blue' : 'yellow'}">${r.status}</span>` },
    { label: 'Channel', render: r => `<span class="badge badge-gray">${r.channel}</span>` },
    {
      label: 'Actions',
      render: r => {
        const ackBtn = r.status !== 'acknowledged'
          ? `<button class="btn-ghost text-sm ack-btn" data-id="${r.id}">Acknowledge</button>`
          : '';
        return `${ackBtn}<button class="btn-ghost text-sm text-red-500 del-btn" data-id="${r.id}" title="Delete">✕</button>`;
      },
    },
  ];
  const table = createDataTable(cols, items, { emptyText: 'No reminders found.' });
  container.appendChild(table);

  table.querySelectorAll('.ack-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      try {
        await reminders.acknowledge(btn.getAttribute('data-id')!);
        showToast('Acknowledged', 'success');
        await reload();
      } catch { showToast('Failed', 'error'); }
    });
  });

  table.querySelectorAll('.del-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const ok = await confirmModal('Delete this reminder?');
      if (!ok) return;
      try {
        await reminders.delete(btn.getAttribute('data-id')!);
        showToast('Reminder deleted', 'success');
        await reload();
      } catch { showToast('Failed to delete', 'error'); }
    });
  });
}
